import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { MediaLibraryDialog } from './MediaLibraryDialog'

type ImagePickerCallback = (url: string) => void

interface ImagePickerContextValue {
    /**
     * Opens the shared media library dialog. The provided callback is invoked
     * with the chosen URL when the user picks (or uploads) an image.
     */
    openImagePicker: (onPick: ImagePickerCallback) => void
}

const ImagePickerContext = createContext<ImagePickerContextValue | null>(null)

export function useImagePicker(): ImagePickerContextValue {
    const ctx = useContext(ImagePickerContext)
    if (!ctx) {
        // Soft fallback so the editor stays usable in isolation (eg. previews)
        return {
            openImagePicker: (onPick) => {
                const url = window.prompt('Image URL')
                if (url) onPick(url)
            },
        }
    }
    return ctx
}

export function ImagePickerProvider({ children }: { children: React.ReactNode }) {
    const [open, setOpen] = useState(false)
    const callbackRef = useRef<ImagePickerCallback | null>(null)

    const openImagePicker = useCallback<ImagePickerContextValue['openImagePicker']>(
        (onPick) => {
            callbackRef.current = onPick
            setOpen(true)
        },
        [],
    )

    const value = useMemo(() => ({ openImagePicker }), [openImagePicker])

    return (
        <ImagePickerContext.Provider value={value}>
            {children}
            <MediaLibraryDialog
                open={open}
                onOpenChange={(next) => {
                    setOpen(next)
                    if (!next) callbackRef.current = null
                }}
                accept="image"
                onSelect={(url) => {
                    const cb = callbackRef.current
                    callbackRef.current = null
                    if (cb) cb(url)
                }}
            />
        </ImagePickerContext.Provider>
    )
}
